/**
 * OPTICS Framework 2026 — Rascal Republic
 *
 * Source: OPTICS_EPR_Form_2026_Team_Company_Weighted.pdf
 * 6 values × 3 assessment areas × 3 contribution lenses = 54 score points per reviewer.
 *
 * This module is additive — does not replace the legacy flat OpticsCriteria
 * (still used by old reviewForms). New magic-link reviewer flow uses this framework.
 */

export type EmployeeLevel = 'general' | 'supervisor' | 'dept_head';

export const EMPLOYEE_LEVEL_LABELS: Record<EmployeeLevel, string> = {
  general:    'General Employee',
  supervisor: 'Supervisor / Manager',
  dept_head:  'Department Head / Senior Leader',
};

export type OpticsLens = 'ind' | 'team' | 'co';

export const OPTICS_LENSES: OpticsLens[] = ['ind', 'team', 'co'];

export const OPTICS_LENS_LABELS: Record<OpticsLens, string> = {
  ind:  'Individual Contribution',
  team: 'Team Contribution',
  co:   'Company Contribution',
};

export const OPTICS_LENS_DESCRIPTIONS: Record<OpticsLens, string> = {
  ind:  'What the employee personally owns, delivers, improves, learns, and follows through on.',
  team: 'How the employee supports team performance, collaboration, communication, trust, and shared accountability.',
  co:   'How the employee contributes beyond their role to cross-functional success, client impact, cost awareness, culture, risk control, and wider business priorities.',
};

export const OPTICS_SCORE_LABELS: Record<number, string> = {
  1: 'Below Standard',
  2: 'Needs Improvement',
  3: 'Meets Expectation',
  4: 'Strong',
  5: 'Role Model',
};

export const OPTICS_SCORE_GUIDANCE: Record<number, string> = {
  1: 'Rarely demonstrates the behavior. Creates risk, delay, conflict, rework, or repeated performance issues.',
  2: 'Demonstrates the behavior inconsistently. Requires reminders, support, or closer supervision to reduce impact on others.',
  3: 'Demonstrates the behavior adequately but not always proactively or consistently across individual, team, and company contribution.',
  4: 'Demonstrates the behavior regularly with minor gaps. Reliable, trusted, and constructive in team settings.',
  5: 'Consistently demonstrates the behavior, strengthens team performance, and positively influences others. Clear examples exist.',
};

export interface OpticsAreaDefinition {
  id: string;            // e.g. "O-A"
  letter: 'A' | 'B' | 'C';
  title: string;
  behaviors: string[];   // observable behaviors verbatim from PDF
}

export interface OpticsValueDefinition {
  code: 'O' | 'P' | 'T' | 'I' | 'C' | 'S';
  title: string;
  intent: string;
  areas: [OpticsAreaDefinition, OpticsAreaDefinition, OpticsAreaDefinition];
}

/** Per-level weighting matrix — applied to (Ind, Team, Co) lens scores per area. */
export const WEIGHTING_BY_LEVEL: Record<EmployeeLevel, { ind: number; team: number; co: number }> = {
  general:    { ind: 0.60, team: 0.25, co: 0.15 },
  supervisor: { ind: 0.40, team: 0.35, co: 0.25 },
  dept_head:  { ind: 0.25, team: 0.35, co: 0.40 },
};

/**
 * The 6 OPTICS values × 3 areas with observable behaviors.
 * Wording locked to PDF spec — do not edit without HR sign-off.
 */
export const OPTICS_FRAMEWORK_2026: readonly OpticsValueDefinition[] = [
  {
    code: 'O',
    title: 'Take Ownership',
    intent: 'Owns commitments, supports team delivery, protects company outcomes through follow-through, problem-solving, and learning.',
    areas: [
      {
        id: 'O-A',
        letter: 'A',
        title: 'Proactive Problem-Solving',
        behaviors: [
          'Identifies issues early and raises them before they affect the team or wider business.',
          'Takes practical action within their role instead of waiting to be chased or instructed.',
          'Brings facts, options, impact on others, and a recommended next step when escalating an issue.',
          'Follows through until the issue is resolved, handed over properly, or clearly owned by another party.',
          'Looks for root causes of recurring problems and helps prevent repeat issues for the team.',
        ],
      },
      {
        id: 'O-B',
        letter: 'B',
        title: 'Accountability for Results',
        behaviors: [
          'Understands what they are responsible for and delivers agreed tasks on time.',
          'Keeps colleagues, managers, and affected teams updated when deadlines, risks, or priorities change.',
          'Owns mistakes without hiding the issue, blaming others, or passing the burden to another team.',
          'Learns from setbacks and changes behavior to protect future delivery.',
          'Measures progress against clear outputs, team needs, and business impact, not effort alone.',
        ],
      },
      {
        id: 'O-C',
        letter: 'C',
        title: 'Growth and Learning',
        behaviors: [
          'Improves the skills needed to perform their role and contribute better to the team.',
          'Seeks feedback and applies it in daily work.',
          'Learns from colleagues, managers, training, and real work situations.',
          'Shows improvement over time in quality, speed, judgment, independence, or collaboration.',
          'Takes responsibility for their own development rather than waiting for formal training only.',
        ],
      },
    ],
  },
  {
    code: 'P',
    title: 'Be Progressive',
    intent: 'Improves how work gets done, adapts constructively, helps team and company move forward.',
    areas: [
      {
        id: 'P-A',
        letter: 'A',
        title: 'Practical Innovation',
        behaviors: [
          'Suggests better ways to complete work, reduce delays, improve quality, or save cost.',
          'Uses new tools, systems, or methods when they genuinely improve team execution.',
          'Challenges outdated ways of working respectfully and with practical alternatives.',
          'Looks beyond routine tasks and considers how the work affects the team, clients, cost, or operations.',
          'Turns ideas into workable actions, not just discussion.',
        ],
      },
      {
        id: 'P-B',
        letter: 'B',
        title: 'Adaptability to Change',
        behaviors: [
          'Responds constructively when priorities, business needs, or work conditions change.',
          'Adjusts quickly without creating unnecessary resistance, confusion, or delay for others.',
          'Stays solution-focused during uncertainty or pressure.',
          'Helps others understand and adapt to change when needed.',
          'Maintains standards and team momentum during periods of transition.',
        ],
      },
      {
        id: 'P-C',
        letter: 'C',
        title: 'Continuous Improvement',
        behaviors: [
          'Reviews current processes and identifies where team execution can improve.',
          'Reduces repeated errors, inefficiencies, handover gaps, or unnecessary manual work.',
          'Participates constructively in feedback, review, and improvement discussions.',
          'Uses lessons learned to improve future work across the team.',
          'Focuses on better outcomes, not only completing tasks in the usual way.',
        ],
      },
    ],
  },
  {
    code: 'T',
    title: 'Apply Transparency',
    intent: 'Communicates clearly, shares information responsibly, raises risks early, helps teams make better decisions.',
    areas: [
      {
        id: 'T-A',
        letter: 'A',
        title: 'Clear and Honest Communication',
        behaviors: [
          'Communicates clearly, respectfully, and directly with colleagues and managers.',
          'Shares concerns early instead of allowing issues to remain hidden.',
          'Gives accurate updates on progress, risks, delays, blockers, and impact on other teams.',
          'Avoids selective reporting, exaggeration, or withholding important information.',
          'Creates trust by being consistent between what they say and what they do.',
        ],
      },
      {
        id: 'T-B',
        letter: 'B',
        title: 'Timely and Appropriate Information Sharing',
        behaviors: [
          'Shares relevant information with the right people at the right time.',
          "Keeps stakeholders informed when their work depends on the employee's input.",
          'Documents or communicates key decisions, updates, and handovers clearly.',
          'Protects confidential information while still supporting proper collaboration.',
          'Avoids information gaps that slow down colleagues, teams, or company priorities.',
        ],
      },
      {
        id: 'T-C',
        letter: 'C',
        title: 'Accountability in Decisions',
        behaviors: [
          'Explains the reason behind decisions, recommendations, or actions when needed.',
          'Takes responsibility for the impact of their decisions on people, work, and business outcomes.',
          'Escalates decisions appropriately when they are outside their authority.',
          'Accepts feedback or challenge without becoming defensive.',
          'Supports agreed decisions and helps the team move forward with clarity.',
        ],
      },
    ],
  },
  {
    code: 'I',
    title: 'Encourage Ideas',
    intent: 'Contributes useful ideas, welcomes different views, helps turn input into practical improvement.',
    areas: [
      {
        id: 'I-A',
        letter: 'A',
        title: 'Openness to Ideas',
        behaviors: [
          'Welcomes suggestions from others regardless of position, department, or seniority.',
          'Listens properly before judging or rejecting an idea.',
          'Encourages practical ideas that improve work, service, safety, cost, client experience, or team performance.',
          'Creates space for others to contribute during discussions or problem-solving.',
          'Responds to ideas respectfully, even when the idea cannot be used.',
        ],
      },
      {
        id: 'I-B',
        letter: 'B',
        title: 'Active Contribution',
        behaviors: [
          'Shares useful ideas based on real work experience, not only general opinions.',
          'Participates actively in discussions, brainstorming, and improvement sessions.',
          'Connects ideas to practical business needs, team constraints, risks, or opportunities.',
          'Helps turn good ideas into workable actions or next steps.',
          'Contributes constructively instead of criticizing without offering alternatives.',
        ],
      },
      {
        id: 'I-C',
        letter: 'C',
        title: 'Recognition and Support for Contributions',
        behaviors: [
          'Gives credit to colleagues who contribute useful ideas or improvements.',
          'Supports good ideas from others instead of competing for ownership.',
          'Helps test, refine, or implement ideas where appropriate.',
          'Encourages quieter team members to share their views.',
          'Builds a team culture where useful contribution is noticed and valued.',
        ],
      },
    ],
  },
  {
    code: 'C',
    title: 'Empower Community',
    intent: 'Builds trust, works across teams, supports colleagues, contributes to a healthy and productive work community.',
    areas: [
      {
        id: 'C-A',
        letter: 'A',
        title: 'Collaboration Across Teams',
        behaviors: [
          'Works well with colleagues across departments, sites, and functions.',
          'Understands how their work affects other teams and the wider business.',
          'Supports shared goals instead of focusing only on their own task or department.',
          'Resolves cross-functional issues constructively and without unnecessary blame.',
          'Builds positive working relationships with internal and external stakeholders.',
        ],
      },
      {
        id: 'C-B',
        letter: 'B',
        title: 'Supportive Team Behavior',
        behaviors: [
          'Treats colleagues with respect, fairness, and professionalism.',
          'Helps others when support is needed and reasonable.',
          'Contributes to a workplace where people feel safe to ask questions or raise concerns.',
          'Gives feedback in a way that helps others improve.',
          'Handles disagreement maturely and keeps the focus on the issue, not the person.',
        ],
      },
      {
        id: 'C-C',
        letter: 'C',
        title: 'Inclusion and Respect for Different Perspectives',
        behaviors: [
          'Listens to different views before forming conclusions.',
          'Ensures relevant voices are included in discussions that affect their work.',
          'Shows respect for different backgrounds, roles, working styles, and levels of experience.',
          'Avoids behavior that excludes, dismisses, or undermines others.',
          'Uses different perspectives to make better decisions and improve team outcomes.',
        ],
      },
    ],
  },
  {
    code: 'S',
    title: 'Practice Sustainability',
    intent: 'Acts responsibly with resources, safety, environment, compliance, company assets, and long-term impact.',
    areas: [
      {
        id: 'S-A',
        letter: 'A',
        title: 'Responsible Decision-Making',
        behaviors: [
          'Considers long-term impact, not only short-term convenience.',
          'Makes decisions that support responsible business, environmental care, and community respect.',
          'Identifies risks that could create waste, damage, unsafe conditions, or reputational issues.',
          'Supports company standards on safety, environment, compliance, and responsible operations.',
          'Acts with care toward company assets, natural resources, clients, and surrounding communities.',
        ],
      },
      {
        id: 'S-B',
        letter: 'B',
        title: 'Resource Efficiency',
        behaviors: [
          'Uses company resources responsibly, including materials, tools, time, energy, and budget.',
          'Avoids unnecessary waste, rework, over-ordering, or careless use of assets.',
          'Looks for practical ways to reduce cost, waste, and inefficiency across the team.',
          'Reports misuse, damage, or avoidable waste when observed.',
          'Supports systems that improve tracking, maintenance, reuse, or conservation of resources.',
        ],
      },
      {
        id: 'S-C',
        letter: 'C',
        title: 'Sustainability Awareness and Influence',
        behaviors: [
          'Understands how sustainability connects to their role and daily work.',
          'Encourages practical sustainable habits within the team.',
          'Shares useful observations or suggestions that improve responsible operations.',
          'Supports company sustainability initiatives through action, not only agreement.',
          'Sets a positive example in how they use resources and make work decisions.',
        ],
      },
    ],
  },
];

/** Total scoreable points per reviewer = 6 values × 3 areas × 3 lenses */
export const OPTICS_TOTAL_SCORE_POINTS = 6 * 3 * 3;

/** Build storage key for an OPTICS score: "O-A-ind", "O-A-team", etc. */
export function opticsScoreKey(areaId: string, lens: OpticsLens): string {
  return `${areaId}-${lens}`;
}

/** Parse a stored score key back to its parts */
export function parseOpticsScoreKey(key: string): { areaId: string; lens: OpticsLens } | null {
  const m = key.match(/^([OPTICS]-[A-C])-(ind|team|co)$/);
  return m ? { areaId: m[1], lens: m[2] as OpticsLens } : null;
}

/** Look up a value definition by code */
export function getOpticsValue(code: string): OpticsValueDefinition | undefined {
  return OPTICS_FRAMEWORK_2026.find(v => v.code === code);
}

/** Look up an area definition by id ("O-A", "P-B", etc.) */
export function getOpticsArea(id: string): OpticsAreaDefinition | undefined {
  for (const v of OPTICS_FRAMEWORK_2026) {
    const a = v.areas.find(a => a.id === id);
    if (a) return a;
  }
  return undefined;
}
